import type { PaymentAdjustmentTypeSectionProps } from '../../types/modals';
import {
  ACCESS_FEE_REASONS,
  APPLICATION_FEE_INVOICE_REASONS,
  CONTRACT_INVOICE_REASONS,
} from '../../constants';
import {
  AdjustmentSelect,
  ChargebackAmountField,
  Checkbox,
  DateField,
  ExternalRefundFields,
  RadioOption,
  SubFieldGroup,
  TextField,
} from './paymentAdjustmentModalFields';

export function PaymentAdjustmentTypeSection({
  tid,
  isContractPayment,
  isDefaultPayment,
  applicantStatus,
  adjustmentType,
  onToggle,
  applySurcharge,
  onApplySurchargeChange,
  adjustmentReason,
  onAdjustmentReasonChange,
  adjustmentSubtype,
  onAdjustmentSubtypeChange,
  chargeRefundFee,
  onChargeRefundFeeChange,
  refundProcessingFee,
  onRefundProcessingFeeChange,
  refundReason,
  onRefundReasonChange,
  isExternalRefund,
  onIsExternalRefundChange,
  externalPaymentSource,
  onExternalPaymentSourceChange,
  externalCheckNumber: _externalCheckNumber,
  onExternalCheckNumberChange,
  chargebackStatus,
  onChargebackStatusChange,
  caseNumber,
  onCaseNumberChange,
  txnNumber,
  onTxnNumberChange,
  caseDate,
  onCaseDateChange,
  chargebackNotificationDate,
  onChargebackNotificationDateChange,
  responseDeadline,
  onResponseDeadlineChange,
  chargebackAmount,
  onChargebackAmountChange,
  onChargebackAmountBlur,
  chargebackType,
  balanceAdjustmentStatus,
  onBalanceAdjustmentStatusChange,
  balanceAdjustmentAction,
  onBalanceAdjustmentActionChange,
  adjustmentLeaseStatus,
  onAdjustmentLeaseStatusChange,
  isCovid,
  onIsCovidChange,
}: PaymentAdjustmentTypeSectionProps) {
  // Angular: DEFAULT / DEFAULT_INVOICE payments only offer the Default Invoice option.
  if (isDefaultPayment) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <Checkbox
            label="Default Invoice"
            checked={adjustmentType === 'default_invoice'}
            onChange={() => onToggle('default_invoice')}
            testId={`${tid}-default-invoice`}
          />
          <div className="min-w-[220px] flex-1">
            <AdjustmentSelect
              value={adjustmentReason}
              onChange={onAdjustmentReasonChange}
              options={[['INVOICE_PREV_DEFAULT', 'Invoice Prev Default']]}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-base font-semibold text-slate-900">
        {isContractPayment ? 'Access Fee' : 'Application Fee'}
      </h3>
      <div className="space-y-2.5">
        <RadioOption
          name={`${tid}-adjustment-type`}
          label="Invoice"
          checked={adjustmentType === 'invoice'}
          onChange={() => onToggle('invoice')}
          testId={`${tid}-invoice`}
        />
        {adjustmentType === 'invoice' && (
          <SubFieldGroup>
            <AdjustmentSelect
              value={adjustmentReason}
              onChange={onAdjustmentReasonChange}
              options={
                isContractPayment
                  ? CONTRACT_INVOICE_REASONS
                  : APPLICATION_FEE_INVOICE_REASONS
              }
            />
            {isContractPayment ? (
              <Checkbox
                label="Add Surcharge"
                checked={applySurcharge}
                onChange={() => onApplySurchargeChange(!applySurcharge)}
                testId={`${tid}-apply-surcharge`}
              />
            ) : null}
          </SubFieldGroup>
        )}
        {isContractPayment ? (
          <>
            <RadioOption
              name={`${tid}-adjustment-type`}
              label="Refund Partial"
              checked={adjustmentType === 'refund_partial'}
              onChange={() => onToggle('refund_partial')}
              testId={`${tid}-refund-partial`}
            />
            {adjustmentType === 'refund_partial' && (
              <SubFieldGroup>
                <AdjustmentSelect
                  value={adjustmentReason}
                  onChange={onAdjustmentReasonChange}
                  options={[
                    ['RENT_DECREASED', 'Rent Decreased'],
                    ['PAYMENT_ISSUE', 'Payment Issue / Re-invoice'],
                    ['OVERPAID', 'Overpaid'],
                  ]}
                />
                <ExternalRefundFields
                  checked={isExternalRefund}
                  onCheckedChange={onIsExternalRefundChange}
                  paymentSource={externalPaymentSource}
                  onPaymentSourceChange={onExternalPaymentSourceChange}
                  onCheckNumberChange={onExternalCheckNumberChange}
                  testIdPrefix={`${tid}-refund-partial`}
                />
              </SubFieldGroup>
            )}
            <RadioOption
              name={`${tid}-adjustment-type`}
              label="Refund Full"
              checked={adjustmentType === 'refund_full'}
              onChange={() => onToggle('refund_full')}
              testId={`${tid}-refund-full`}
            />
            {adjustmentType === 'refund_full' && (
              <SubFieldGroup>
                <Checkbox
                  label="Cancel Certificate"
                  checked={adjustmentSubtype === 'cancel_certificate'}
                  onChange={() =>
                    onAdjustmentSubtypeChange(
                      adjustmentSubtype === 'cancel_certificate'
                        ? ''
                        : 'cancel_certificate',
                    )
                  }
                  testId={`${tid}-cancel-certificate`}
                />
                <div className="grid gap-2 sm:grid-cols-2">
                  <Checkbox
                    label="Charge Refund Fee"
                    checked={chargeRefundFee}
                    onChange={() => onChargeRefundFeeChange(!chargeRefundFee)}
                    testId={`${tid}-charge-refund-fee`}
                  />
                  <Checkbox
                    label="Refund Processing Fee"
                    checked={refundProcessingFee}
                    onChange={() =>
                      onRefundProcessingFeeChange(!refundProcessingFee)
                    }
                    testId={`${tid}-refund-processing-fee`}
                  />
                </div>
                <AdjustmentSelect
                  value={refundReason}
                  onChange={onRefundReasonChange}
                  options={ACCESS_FEE_REASONS.map((reason) => [
                    reason,
                    reason,
                  ])}
                />
                <ExternalRefundFields
                  checked={isExternalRefund}
                  onCheckedChange={onIsExternalRefundChange}
                  paymentSource={externalPaymentSource}
                  onPaymentSourceChange={onExternalPaymentSourceChange}
                  onCheckNumberChange={onExternalCheckNumberChange}
                  testIdPrefix={`${tid}-refund-full`}
                />
              </SubFieldGroup>
            )}
          </>
        ) : (
          <>
            <RadioOption
              name={`${tid}-adjustment-type`}
              label="Refund"
              checked={adjustmentType === 'refund'}
              onChange={() => onToggle('refund')}
              testId={`${tid}-refund`}
            />
            {adjustmentType === 'refund' && (
              <SubFieldGroup>
                <AdjustmentSelect
                  value={adjustmentReason}
                  onChange={onAdjustmentReasonChange}
                  options={[
                    ['APPLICATION_DENIED', 'Application Denied'],
                    ['OVERPAYMENT', 'Overpayment'],
                    ['CANCELLED', 'Cancelled'],
                  ]}
                />
                <ExternalRefundFields
                  checked={isExternalRefund}
                  onCheckedChange={onIsExternalRefundChange}
                  paymentSource={externalPaymentSource}
                  onPaymentSourceChange={onExternalPaymentSourceChange}
                  onCheckNumberChange={onExternalCheckNumberChange}
                  testIdPrefix={`${tid}-refund`}
                />
              </SubFieldGroup>
            )}
          </>
        )}
        <RadioOption
          name={`${tid}-adjustment-type`}
          label="Chargeback"
          checked={adjustmentType === 'chargeback'}
          onChange={() => onToggle('chargeback')}
          testId={`${tid}-chargeback`}
        />
        {adjustmentType === 'chargeback' && (
          <SubFieldGroup>
            {applicantStatus ? (
              <p
                className="text-sm font-medium text-slate-600"
                data-testid={`${tid}-chargeback-application-status`}
              >
                Application Status: {applicantStatus}
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <AdjustmentSelect
                value={chargebackStatus}
                onChange={onChargebackStatusChange}
                placeholder="Select CB Status"
                options={[
                  ['INVESTIGATING', 'Investigating'],
                  ['CHALLENGED', 'Challenged'],
                  ['CHARGEBACK_COMPLETE', 'Chargeback Complete'],
                ]}
              />
              <AdjustmentSelect
                value={adjustmentReason}
                onChange={onAdjustmentReasonChange}
                options={[
                  ['NOT_AUTHORIZED', 'Not Authorized'],
                  ['CONTESTING_CHARGES', 'Contesting Charges'],
                  ['BANK_HOLD', 'Bank Hold'],
                  ['OTHER', 'Other'],
                ]}
              />
              <TextField
                label="Case No."
                value={caseNumber}
                onChange={onCaseNumberChange}
              />
              <TextField
                label="Transaction No."
                value={txnNumber}
                onChange={onTxnNumberChange}
              />
              <DateField
                label="Case Date"
                value={caseDate}
                onChange={onCaseDateChange}
                maxDate={new Date()}
              />
              <DateField
                label="Received Date"
                value={chargebackNotificationDate}
                onChange={onChargebackNotificationDateChange}
                popperPlacement="bottom-end"
                maxDate={new Date()}
              />
              <ChargebackAmountField
                label="Chargeback Amount"
                value={chargebackAmount}
                onChange={onChargebackAmountChange}
                onBlur={onChargebackAmountBlur}
                showMinusPrefix={chargebackType !== 'WON_CREDIT'}
              />
              <DateField
                label="Response Deadline*"
                value={responseDeadline}
                onChange={onResponseDeadlineChange}
                popperPlacement="bottom-end"
                allowFutureDates
              />
            </div>
          </SubFieldGroup>
        )}
        {isContractPayment && (
          <RadioOption
            name={`${tid}-adjustment-type`}
            label="Balance Adjustment"
            checked={adjustmentType === 'balance_adjustment'}
            onChange={() => onToggle('balance_adjustment')}
            testId={`${tid}-balance-adjustment`}
          />
        )}
        {adjustmentType === 'balance_adjustment' && (
          <SubFieldGroup>
            <AdjustmentSelect
              value={balanceAdjustmentStatus}
              onChange={onBalanceAdjustmentStatusChange}
              placeholder="Select Status"
              options={[
                ['INVESTIGATING', 'Investigating'],
                ['CANCELLED', 'Cancelled'],
                ['COMPLETED', 'Completed'],
              ]}
            />
            <AdjustmentSelect
              value={balanceAdjustmentAction}
              onChange={onBalanceAdjustmentActionChange}
              placeholder="Select Action"
              options={[
                ['WRITE-OFF', 'Write-off'],
                ['ADJUST', 'Adjust'],
              ]}
            />
            <AdjustmentSelect
              value={adjustmentLeaseStatus}
              onChange={onAdjustmentLeaseStatusChange}
              placeholder="Lease Status"
              options={[
                ['ACTIVE', 'Active'],
                ['EXPIRED', 'Expired'],
                ['PENDING', 'Pending'],
              ]}
            />
          </SubFieldGroup>
        )}

        {!(adjustmentType === 'invoice' && isContractPayment) && (
          <div className="pt-2">
            <Checkbox
              label="Due to Covid-19"
              checked={isCovid}
              onChange={() => onIsCovidChange(!isCovid)}
              testId={`${tid}-covid`}
            />
          </div>
        )}
      </div>
    </section>
  );
}
